/**
 * ReminderTemplateEditController — page /relances-modeles-edition.html.
 *
 * Pilote l'édition d'un template de relance — création (`?new=1`) ou mise
 * à jour (`?id=<uuid>`). Conformément à §2.F :
 *   - Mode édition : 5 blocs Paramètres + 4 variantes (B2B/B2C ×
 *     impayée/partielle), chacun sauvegardé indépendamment via PUT partiel.
 *   - Mode création (EPIC-099 / 99.18) : seuls 2 blocs visibles —
 *     Paramètres + un unique bloc Contenu (titre + corps + placeholders).
 *     À la validation, le titre/corps est dupliqué × 4 vers les 4 variantes
 *     dans le payload POST, puis redirection vers `?id=<uuid>`.
 *   - Insertion de placeholder et aperçu rendu sont scopés au bloc parent
 *     (résolution via `closest('[data-edit-block]')`).
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const PLACEHOLDERS = [
  "numero_facture",
  "date_emission",
  "date_echeance",
  "jours_retard",
  "montant_total",
  "montant_paye",
  "montant_restant_du",
  "lien_paiement",
  "nom_client",
  "nom_emetteur",
  "numero_relance",
  "indemnite_forfaitaire",
  "penalites_retard",
  "total_du_avec_penalites",
];

const PLACEHOLDER_FAKES = {
  numero_facture: "F-2026-013",
  date_emission: "25/04/2026",
  date_echeance: "25/05/2026",
  jours_retard: "7",
  montant_total: "3 720,00 €",
  montant_paye: "1 200,00 €",
  montant_restant_du: "2 520,00 €",
  lien_paiement: "https://wero.example/pay/abc123",
  nom_client: "Acme Corp",
  nom_emetteur: "Atelier Marie Dupont",
  numero_relance: "2",
  indemnite_forfaitaire: "40,00 €",
  penalites_retard: "12,40 €",
  total_du_avec_penalites: "2 572,40 €",
};

const PREVIEW_DEBOUNCE_MS = 300;
const SUMMARY_TITLE_MAX = 80;
const SUMMARY_EXCERPT_MAX = 120;

const VARIANT_BLOCKS = ["b2b-imp", "b2b-par", "b2c-imp", "b2c-par"];
const ALL_BLOCKS = ["settings", ...VARIANT_BLOCKS];

// Mapping bloc → champs payload (clés camelCase API) et targets Stimulus.
const BLOCK_FIELDS = {
  "settings": [
    { payload: "delayDays", target: "delayInput", kind: "delay" },
    { payload: "status",    target: "statusSelect", kind: "status" },
  ],
  "b2b-imp": [
    { payload: "titleImpayeB2b", target: "titleImpayeB2bInput", kind: "title" },
    { payload: "textImpayeB2b",  target: "textImpayeB2bInput",  kind: "text"  },
  ],
  "b2b-par": [
    { payload: "titlePartielB2b", target: "titlePartielB2bInput", kind: "title" },
    { payload: "textPartielB2b",  target: "textPartielB2bInput",  kind: "text"  },
  ],
  "b2c-imp": [
    { payload: "titleImpayeB2c", target: "titleImpayeB2cInput", kind: "title" },
    { payload: "textImpayeB2c",  target: "textImpayeB2cInput",  kind: "text"  },
  ],
  "b2c-par": [
    { payload: "titlePartielB2c", target: "titlePartielB2cInput", kind: "title" },
    { payload: "textPartielB2c",  target: "textPartielB2cInput",  kind: "text"  },
  ],
};

const BLOCK_LABELS = {
  "settings": "Paramètres",
  "b2b-imp":  "B2B impayée",
  "b2b-par":  "B2B partielle",
  "b2c-imp":  "B2C impayée",
  "b2c-par":  "B2C partielle",
};

// Bloc → suffixe target (PascalCase) — déterminé statiquement pour rester
// compatible avec la déclaration `static targets`.
const BLOCK_TARGETS = {
  "settings": "settings",
  "b2b-imp":  "b2bImp",
  "b2b-par":  "b2bPar",
  "b2c-imp":  "b2cImp",
  "b2c-par":  "b2cPar",
};

export default class ReminderTemplateEditController extends BaseController {
  static targets = [
    "crumbTitle",
    "pageTitle",
    "statusBadge",
    "delayInput",
    "delayLabel",
    "statusSelect",
    "loadError",
    // Blocs + boutons par bloc.
    "settingsBlock", "settingsOpenBtn", "settingsCancelBtn", "settingsSaveBtn",
    "settingsSummary", "settingsError",
    "b2bImpBlock", "b2bImpOpenBtn", "b2bImpCancelBtn", "b2bImpSaveBtn",
    "b2bImpSummary", "b2bImpError",
    "b2bParBlock", "b2bParOpenBtn", "b2bParCancelBtn", "b2bParSaveBtn",
    "b2bParSummary", "b2bParError",
    "b2cImpBlock", "b2cImpOpenBtn", "b2cImpCancelBtn", "b2cImpSaveBtn",
    "b2cImpSummary", "b2cImpError",
    "b2cParBlock", "b2cParOpenBtn", "b2cParCancelBtn", "b2cParSaveBtn",
    "b2cParSummary", "b2cParError",
    // Inputs variantes.
    "titleImpayeB2bInput",  "textImpayeB2bInput",
    "titlePartielB2bInput", "textPartielB2bInput",
    "titleImpayeB2cInput",  "textImpayeB2cInput",
    "titlePartielB2cInput", "textPartielB2cInput",
    // Création (99.18) : bloc Contenu unifié, dupliqué × 4 au POST.
    "contentBlock", "titleNewInput", "textNewInput", "contentError",
    "createSubmitBtn",
    // Statut archivé : hint global au-dessus des variantes (§2.F / 99.17).
    "archivedVariantsHint",
  ];

  templateId = null;
  isNew = true;
  _previewTimeout = null;
  // Map<blockId, {field: value, ...}> — snapshot avant édition pour Annuler.
  _snapshots = new Map();

  async connect() {
    const params = new URLSearchParams(window.location.search);
    const idParam = params.get("id");
    const isNew = params.get("new") === "1";

    if (idParam) {
      this.templateId = idParam;
      this.isNew = false;
      try {
        // EPIC-099.23 — `includeArchived: true` pour permettre l'ouverture
        // directe d'un template archivé (flot de réactivation 99.17).
        // Sans ce flag, l'archivé est absent de la liste retournée → redirect
        // silencieuse en dessous, fonctionnalité de réactivation inaccessible.
        const data = await api.listReminderTemplates({ includeArchived: true });
        const found = (data?.templates || []).find((t) => t.id === idParam);
        if (!found) {
          this._showLoadError("Template introuvable. Retour à la liste.");
          window.location.assign("/relances-modeles.html");
          return;
        }
        this._populateFromTemplate(found);
        this._refreshAllSummaries();
        this._enterEditMode();
      } catch (_error) {
        this._showLoadError("Impossible de charger le template.");
      }
    } else {
      this.templateId = null;
      this.isNew = true;
      if (!isNew) {
        // URL sans paramètres : on retombe sur la création.
      }
      this._populateNewDefaults();
      this._enterCreateMode();
    }

    this.schedulePreview();
  }

  // ---------------------------------------------------------------------------
  // Mode helpers
  // ---------------------------------------------------------------------------

  _enterEditMode() {
    // Édition : variantes visibles, bloc Contenu caché. Tous les blocs en
    // vue résumé (bouton « Modifier » par bloc).
    this._toggleModeBlocks(false);
    for (const block of ALL_BLOCKS) {
      this._closeBlock(block);
    }
  }

  _enterCreateMode() {
    // Création : variantes cachées, bloc Contenu visible. Le bloc Paramètres
    // est ouvert en édition sans footer (les valeurs sont lues directement
    // au POST, pas de PATCH partiel possible avant existence du template).
    this._toggleModeBlocks(true);
    this._openBlockUI("settings", { hideActions: true });
  }

  _toggleModeBlocks(isNew) {
    this.element.querySelectorAll("[data-new-mode-hidden]").forEach((el) => {
      el.hidden = isNew;
    });
    this.element.querySelectorAll("[data-new-mode-only]").forEach((el) => {
      el.hidden = !isNew;
    });
  }

  // ---------------------------------------------------------------------------
  // UI events — open/cancel/save par bloc
  // ---------------------------------------------------------------------------

  openBlock(event) {
    event.preventDefault();
    const blockId = this._blockIdFromEvent(event);
    if (!blockId) return;
    this._snapshot(blockId);
    this._openBlockUI(blockId, { hideActions: false });
    this._focusFirstInput(blockId);
  }

  cancelBlock(event) {
    event.preventDefault();
    const blockId = this._blockIdFromEvent(event);
    if (!blockId) return;
    this._restoreSnapshot(blockId);
    this._closeBlock(blockId);
    this._clearError(blockId);
    this._refreshSummary(blockId);
  }

  async saveBlock(event) {
    if (event) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    const blockId = this._blockIdFromEvent(event);
    if (!blockId) return;

    // Mode création : un submit dans un bloc déclenche la création globale.
    if (this.isNew) {
      this.createTemplate(event);
      return;
    }

    if (!this.templateId) return;

    this._clearError(blockId);
    const partial = this._collectBlockPayload(blockId);
    if (partial === null) return; // erreur déjà affichée

    this._setBlockSaving(blockId, true);
    try {
      const updated = await api.updateReminderTemplate(this.templateId, partial);
      this.showSuccess(`Bloc « ${BLOCK_LABELS[blockId]} » enregistré.`);
      if (updated) this._populateFromTemplate(updated);
      this._refreshSummary(blockId);
      this._closeBlock(blockId);
    } catch (error) {
      const msg = (error instanceof APIError && error.message)
        ? error.message
        : "Erreur lors de la sauvegarde.";
      this._showBlockError(blockId, msg);
    } finally {
      this._setBlockSaving(blockId, false);
    }
  }

  async createTemplate(event) {
    if (event) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    if (!this.isNew) return;

    this._clearContentError();
    const payload = this._collectCreatePayload();
    if (payload === null) return;

    this._setCreateDisabled(true);
    try {
      const created = await api.createReminderTemplate(payload);
      this.showSuccess("Template créé.");
      if (created && created.id) {
        this.templateId = created.id;
        this.isNew = false;
        const url = new URL(window.location.href);
        url.searchParams.delete("new");
        url.searchParams.set("id", created.id);
        window.history.replaceState({}, "", url.toString());
        this._populateFromTemplate(created);
        this._refreshAllSummaries();
        this._enterEditMode();
      }
    } catch (error) {
      const msg = (error instanceof APIError && error.message)
        ? error.message
        : "Erreur lors de la création.";
      this._showContentError(msg);
    } finally {
      this._setCreateDisabled(false);
    }
  }

  // ---------------------------------------------------------------------------
  // UI events — placeholder & preview, scopés au bloc parent.
  // ---------------------------------------------------------------------------

  insertPlaceholder(event) {
    event.preventDefault();
    const blockEl = event.target.closest("[data-edit-block]");
    if (!blockEl) return;
    const blockId = blockEl.dataset.block;
    if (!VARIANT_BLOCKS.includes(blockId) && blockId !== "content") return;

    const select = blockEl.querySelector("[data-placeholder-select]");
    const textarea = blockEl.querySelector("textarea");
    if (!(select instanceof HTMLSelectElement) || !(textarea instanceof HTMLTextAreaElement)) return;

    const placeholder = select.value;
    if (!placeholder || !PLACEHOLDERS.includes(placeholder)) return;

    const insertion = `{{${placeholder}}}`;
    const start = textarea.selectionStart ?? textarea.value.length;
    const end = textarea.selectionEnd ?? textarea.value.length;
    textarea.value = textarea.value.slice(0, start) + insertion + textarea.value.slice(end);
    const cursor = start + insertion.length;
    textarea.setSelectionRange(cursor, cursor);
    textarea.focus();
    select.value = "";
    this.schedulePreview();
  }

  togglePreview(event) {
    event.preventDefault();
    const blockEl = event.target.closest("[data-edit-block]");
    if (!blockEl) return;
    const previewBlock = blockEl.querySelector("[data-preview-block]");
    if (!previewBlock) return;
    previewBlock.hidden = !previewBlock.hidden;
    if (!previewBlock.hidden) {
      this._renderPreviewForBlock(blockEl);
    }
  }

  schedulePreview() {
    if (this._previewTimeout) {
      clearTimeout(this._previewTimeout);
    }
    this._previewTimeout = setTimeout(() => this._renderAllPreviews(), PREVIEW_DEBOUNCE_MS);
    this._updateDelayLabel();
  }

  // ---------------------------------------------------------------------------
  // Hydration / payload
  // ---------------------------------------------------------------------------

  _populateFromTemplate(tpl) {
    if (this.hasDelayInputTarget) this.delayInputTarget.value = String(tpl.delayDays ?? "");
    if (this.hasStatusSelectTarget) {
      this.statusSelectTarget.value = tpl.status === "archived" ? "archived" : "active";
    }
    if (this.hasTitleImpayeB2bInputTarget) this.titleImpayeB2bInputTarget.value = tpl.titleImpayeB2b || "";
    if (this.hasTextImpayeB2bInputTarget)  this.textImpayeB2bInputTarget.value  = tpl.textImpayeB2b  || "";
    if (this.hasTitlePartielB2bInputTarget) this.titlePartielB2bInputTarget.value = tpl.titlePartielB2b || "";
    if (this.hasTextPartielB2bInputTarget)  this.textPartielB2bInputTarget.value  = tpl.textPartielB2b  || "";
    if (this.hasTitleImpayeB2cInputTarget) this.titleImpayeB2cInputTarget.value = tpl.titleImpayeB2c || "";
    if (this.hasTextImpayeB2cInputTarget)  this.textImpayeB2cInputTarget.value  = tpl.textImpayeB2c  || "";
    if (this.hasTitlePartielB2cInputTarget) this.titlePartielB2cInputTarget.value = tpl.titlePartielB2c || "";
    if (this.hasTextPartielB2cInputTarget)  this.textPartielB2cInputTarget.value  = tpl.textPartielB2c  || "";

    this._refreshTitles(tpl);
    this._refreshStatusBadge(tpl.status);
    this._refreshArchivedLock(tpl.status);
  }

  /**
   * Désactive les boutons « Modifier » des 4 blocs Variante quand le template
   * est archivé (§2.F / 99.17 : seuls `status` et `delayDays` sont éditables
   * tant que le template n'est pas réactivé). Affiche aussi le hint global.
   *
   * Appelé à chaque `_populateFromTemplate` — couvre le chargement initial
   * et le rafraîchissement post-save du bloc Paramètres (réactivation).
   *
   * @param {string|null|undefined} status `"archived"` ou `"active"`.
   */
  _refreshArchivedLock(status) {
    const isArchived = status === "archived";
    if (this.hasArchivedVariantsHintTarget) {
      this.archivedVariantsHintTarget.hidden = !isArchived;
    }
    for (const blockId of VARIANT_BLOCKS) {
      const openBtn = this._openBtn(blockId);
      if (!openBtn) continue;
      openBtn.disabled = isArchived;
      openBtn.setAttribute("aria-disabled", isArchived ? "true" : "false");
      if (isArchived) {
        openBtn.title = "Réactivez ce template pour modifier son contenu.";
      } else {
        openBtn.removeAttribute("title");
      }
    }
  }

  _populateNewDefaults() {
    if (this.hasDelayInputTarget && !this.delayInputTarget.value) this.delayInputTarget.value = "7";
    if (this.hasTitleNewInputTarget && !this.titleNewInputTarget.value) {
      this.titleNewInputTarget.value = "Relance : facture {{numero_facture}}";
    }
    if (this.hasTextNewInputTarget && !this.textNewInputTarget.value) {
      this.textNewInputTarget.value = `Bonjour {{nom_client}},\n\n`
        + `Concernant la facture {{numero_facture}} émise le {{date_emission}} `
        + `et échue le {{date_echeance}}, il reste {{montant_restant_du}} à régler.\n\n`
        + `Cordialement,\n{{nom_emetteur}}`;
    }
  }

  _collectBlockPayload(blockId) {
    const fields = BLOCK_FIELDS[blockId];
    if (!fields) return null;
    const out = {};
    for (const f of fields) {
      const value = this._readField(f.target);
      if (f.kind === "delay") {
        const num = Number.parseInt(value, 10);
        if (!Number.isFinite(num) || num < 0 || num > 180) {
          this._showBlockError(blockId, "Le délai doit être un entier entre 0 et 180 jours.");
          return null;
        }
        out[f.payload] = num;
      } else if (f.kind === "status") {
        if (value !== "active" && value !== "archived") {
          this._showBlockError(blockId, "Statut invalide.");
          return null;
        }
        out[f.payload] = value;
      } else {
        if (!value || !value.trim()) {
          this._showBlockError(blockId, `Le champ « ${this._humanizeField(f.payload)} » est requis.`);
          return null;
        }
        const invalid = this._findInvalidPlaceholder(value);
        if (invalid !== null) {
          this._showBlockError(blockId, `Placeholder inconnu : {{${invalid}}}.`);
          return null;
        }
        out[f.payload] = value;
      }
    }
    return out;
  }

  /**
   * Mode création (99.18) : payload composé de `delayDays` (lu directement
   * du bloc Paramètres, pas de PATCH partiel intermédiaire) + 8 champs des
   * 4 variantes, tous initialisés au même couple titre/corps saisi dans le
   * bloc Contenu unifié. Le `status` n'est pas accepté par POST (toujours
   * `active` à la création côté backend).
   */
  _collectCreatePayload() {
    const settings = this._collectBlockPayload("settings");
    if (settings === null) {
      this._showContentError("Vérifiez le délai et le statut du bloc Paramètres.");
      return null;
    }

    const title = this._readField("titleNewInput").trim();
    const text  = this._readField("textNewInput").trim();
    if (!title) {
      this._showContentError("Le titre est requis.");
      return null;
    }
    if (!text) {
      this._showContentError("Le corps de l'email est requis.");
      return null;
    }
    const invalid = this._findInvalidPlaceholder(title) ?? this._findInvalidPlaceholder(text);
    if (invalid !== null) {
      this._showContentError(`Placeholder inconnu : {{${invalid}}}.`);
      return null;
    }

    return {
      delayDays: settings.delayDays,
      titleImpayeB2b: title,  textImpayeB2b: text,
      titlePartielB2b: title, textPartielB2b: text,
      titleImpayeB2c: title,  textImpayeB2c: text,
      titlePartielB2c: title, textPartielB2c: text,
    };
  }

  // ---------------------------------------------------------------------------
  // Snapshot / restore (Annuler)
  // ---------------------------------------------------------------------------

  _snapshot(blockId) {
    const fields = BLOCK_FIELDS[blockId];
    if (!fields) return;
    const snap = {};
    for (const f of fields) {
      snap[f.target] = this._readField(f.target);
    }
    this._snapshots.set(blockId, snap);
  }

  _restoreSnapshot(blockId) {
    const snap = this._snapshots.get(blockId);
    if (!snap) return;
    for (const [target, value] of Object.entries(snap)) {
      const el = this[`${target}Target`];
      if (el) el.value = value;
    }
    this._snapshots.delete(blockId);
  }

  // ---------------------------------------------------------------------------
  // Preview
  // ---------------------------------------------------------------------------

  _renderAllPreviews() {
    const blockEls = VARIANT_BLOCKS
      .map((id) => this._blockElement(id))
      .filter((el) => el);
    if (this.hasContentBlockTarget) blockEls.push(this.contentBlockTarget);
    for (const blockEl of blockEls) {
      const preview = blockEl.querySelector("[data-preview-block]");
      if (!preview || preview.hidden) continue;
      this._renderPreviewForBlock(blockEl);
    }
    this._updateDelayLabel();
  }

  _renderPreviewForBlock(blockEl) {
    const titleEl = blockEl.querySelector("[data-preview-title]");
    const bodyEl  = blockEl.querySelector("[data-preview-body]");
    const titleInput = blockEl.querySelector('input[type="text"]');
    const bodyInput  = blockEl.querySelector("textarea");
    const title = (titleInput && titleInput.value) || "";
    const body  = (bodyInput && bodyInput.value)  || "";
    if (titleEl) titleEl.textContent = this._substitute(title);
    if (bodyEl)  bodyEl.textContent  = this._substitute(body);
  }

  _substitute(text) {
    if (!text) return "";
    return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      if (Object.prototype.hasOwnProperty.call(PLACEHOLDER_FAKES, key)) {
        return PLACEHOLDER_FAKES[key];
      }
      return match;
    });
  }

  _updateDelayLabel() {
    if (this.hasDelayLabelTarget && this.hasDelayInputTarget) {
      const v = Number.parseInt(this.delayInputTarget.value, 10);
      this.delayLabelTarget.textContent = Number.isFinite(v) ? String(v) : "—";
    }
  }

  // ---------------------------------------------------------------------------
  // Block UI state machine
  // ---------------------------------------------------------------------------

  _openBlockUI(blockId, { hideActions }) {
    const blockEl = this._blockElement(blockId);
    if (!blockEl) return;
    const summary = blockEl.querySelector("[data-edit-summary]");
    const form    = blockEl.querySelector("[data-edit-form]");
    const openBtn   = this._openBtn(blockId);
    const cancelBtn = this._cancelBtn(blockId);
    const saveBtn   = this._saveBtn(blockId);
    const actions   = blockEl.querySelector(".nm-edit-block__actions");
    if (summary) summary.hidden = true;
    if (form)    form.hidden    = false;
    if (openBtn) openBtn.hidden = true;
    if (cancelBtn) cancelBtn.hidden = hideActions;
    if (saveBtn)   saveBtn.hidden   = hideActions;
    if (actions)   actions.hidden   = hideActions;
    blockEl.dataset.editState = "editing";
  }

  _closeBlock(blockId) {
    const blockEl = this._blockElement(blockId);
    if (!blockEl) return;
    const summary = blockEl.querySelector("[data-edit-summary]");
    const form    = blockEl.querySelector("[data-edit-form]");
    const openBtn   = this._openBtn(blockId);
    const cancelBtn = this._cancelBtn(blockId);
    const saveBtn   = this._saveBtn(blockId);
    const actions   = blockEl.querySelector(".nm-edit-block__actions");
    if (summary) summary.hidden = false;
    if (form)    form.hidden    = true;
    if (openBtn) openBtn.hidden = false;
    if (cancelBtn) cancelBtn.hidden = true;
    if (saveBtn)   saveBtn.hidden   = false;
    if (actions)   actions.hidden   = false;
    blockEl.dataset.editState = "summary";
  }

  _focusFirstInput(blockId) {
    const blockEl = this._blockElement(blockId);
    if (!blockEl) return;
    const input = blockEl.querySelector("input, select, textarea");
    if (input instanceof HTMLElement) input.focus();
  }

  // ---------------------------------------------------------------------------
  // Summaries
  // ---------------------------------------------------------------------------

  _refreshAllSummaries() {
    for (const blockId of ALL_BLOCKS) {
      this._refreshSummary(blockId);
    }
  }

  _refreshSummary(blockId) {
    if (blockId === "settings") {
      this._refreshSettingsSummary();
      return;
    }
    this._refreshVariantSummary(blockId);
  }

  _refreshSettingsSummary() {
    if (!this.hasSettingsSummaryTarget) return;
    const delay = this.hasDelayInputTarget ? Number.parseInt(this.delayInputTarget.value, 10) : null;
    const status = this.hasStatusSelectTarget ? this.statusSelectTarget.value : "active";
    const statusLabel = status === "archived" ? "archivé" : "actif";
    const delayLabel = Number.isFinite(delay) ? `J+${delay}` : "—";
    this.settingsSummaryTarget.textContent = `${delayLabel} · ${statusLabel}`;
  }

  _refreshVariantSummary(blockId) {
    const fields = BLOCK_FIELDS[blockId];
    if (!fields) return;
    const summaryTarget = this[`${BLOCK_TARGETS[blockId]}SummaryTarget`];
    if (!summaryTarget) return;
    const titleField = fields.find((f) => f.kind === "title");
    const textField  = fields.find((f) => f.kind === "text");
    const title = titleField ? this._readField(titleField.target) : "";
    const body  = textField  ? this._readField(textField.target)  : "";
    const titleEl = summaryTarget.querySelector("[data-summary-title]");
    const bodyEl  = summaryTarget.querySelector("[data-summary-excerpt]");
    if (titleEl) titleEl.textContent = this._truncate(title || "—", SUMMARY_TITLE_MAX);
    if (bodyEl)  bodyEl.textContent  = this._truncate(this._stripLines(body || "—"), SUMMARY_EXCERPT_MAX);
  }

  _truncate(s, max) {
    if (!s) return "—";
    return s.length > max ? `${s.slice(0, max).trimEnd()}…` : s;
  }

  _stripLines(s) {
    return s.replace(/\s+/g, " ").trim();
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _findInvalidPlaceholder(text) {
    const matches = text.matchAll(/\{\{(\w+)\}\}/g);
    for (const m of matches) {
      const name = m[1];
      if (!PLACEHOLDERS.includes(name)) return name;
    }
    return null;
  }

  _readField(targetName) {
    const el = this[`${targetName}Target`];
    if (!el) return "";
    return el.value || "";
  }

  _humanizeField(camelKey) {
    const labels = {
      titleImpayeB2b: "Titre B2B impayée",
      textImpayeB2b: "Corps B2B impayée",
      titlePartielB2b: "Titre B2B partielle",
      textPartielB2b: "Corps B2B partielle",
      titleImpayeB2c: "Titre B2C impayée",
      textImpayeB2c: "Corps B2C impayée",
      titlePartielB2c: "Titre B2C partielle",
      textPartielB2c: "Corps B2C partielle",
    };
    return labels[camelKey] || camelKey;
  }

  _blockIdFromEvent(event) {
    const el = event.target?.closest("[data-edit-block]");
    if (el && el.dataset.block) return el.dataset.block;
    return null;
  }

  _blockElement(blockId) {
    const target = this[`${BLOCK_TARGETS[blockId]}BlockTarget`];
    return target || null;
  }

  _openBtn(blockId)   { return this[`${BLOCK_TARGETS[blockId]}OpenBtnTarget`]   || null; }
  _cancelBtn(blockId) { return this[`${BLOCK_TARGETS[blockId]}CancelBtnTarget`] || null; }
  _saveBtn(blockId)   { return this[`${BLOCK_TARGETS[blockId]}SaveBtnTarget`]   || null; }

  _setBlockSaving(blockId, saving) {
    const saveBtn   = this._saveBtn(blockId);
    const cancelBtn = this._cancelBtn(blockId);
    if (saveBtn) {
      saveBtn.disabled = saving;
      saveBtn.textContent = saving ? "Enregistrement…" : "Enregistrer ce bloc";
    }
    if (cancelBtn) cancelBtn.disabled = saving;
  }

  _showBlockError(blockId, message) {
    const errEl = this[`${BLOCK_TARGETS[blockId]}ErrorTarget`];
    if (!errEl) {
      this.showError(message);
      return;
    }
    errEl.textContent = message;
    errEl.hidden = false;
  }

  _clearError(blockId) {
    const errEl = this[`${BLOCK_TARGETS[blockId]}ErrorTarget`];
    if (!errEl) return;
    errEl.textContent = "";
    errEl.hidden = true;
  }

  _showLoadError(message) {
    if (!this.hasLoadErrorTarget) {
      this.showError(message);
      return;
    }
    this.loadErrorTarget.textContent = message;
    this.loadErrorTarget.hidden = false;
  }

  _showContentError(message) {
    if (!this.hasContentErrorTarget) {
      this.showError(message);
      return;
    }
    this.contentErrorTarget.textContent = message;
    this.contentErrorTarget.hidden = false;
  }

  _clearContentError() {
    if (!this.hasContentErrorTarget) return;
    this.contentErrorTarget.textContent = "";
    this.contentErrorTarget.hidden = true;
  }

  _setCreateDisabled(disabled) {
    if (this.hasCreateSubmitBtnTarget) {
      this.createSubmitBtnTarget.disabled = disabled;
      this.createSubmitBtnTarget.textContent = disabled ? "Création…" : "Créer ce template";
    }
  }

  _refreshTitles(tpl) {
    const delay = tpl?.delayDays ?? this._readDelay();
    const label = `Template J+${delay}`;
    if (this.hasPageTitleTarget) this.pageTitleTarget.textContent = label;
    if (this.hasCrumbTitleTarget) this.crumbTitleTarget.textContent = label;
    this._updateDelayLabel();
  }

  _refreshStatusBadge(status) {
    if (!this.hasStatusBadgeTarget) return;
    const isArchived = status === "archived";
    this.statusBadgeTarget.textContent = "";
    const dot = document.createElement("span");
    dot.className = "nm-pill__dot";
    this.statusBadgeTarget.appendChild(dot);
    this.statusBadgeTarget.appendChild(document.createTextNode(isArchived ? "archivé" : "actif"));
    this.statusBadgeTarget.classList.toggle("nm-pill--olive", !isArchived);
    this.statusBadgeTarget.classList.toggle("nm-pill--muted", isArchived);
  }

  _readDelay() {
    if (!this.hasDelayInputTarget) return 0;
    const v = Number.parseInt(this.delayInputTarget.value, 10);
    return Number.isFinite(v) ? v : 0;
  }
}
